import { Controller } from "@hotwired/stimulus";

const WIDTH_STORAGE_KEY = "backoffice:detail-panel-width";
const WIDTH_PROPERTY = "--detail-panel-width";
const MIN_WIDTH = 360;
const KEYBOARD_STEP = 32;

// Whether the board already has a panel on screen. Module-level, not
// per-instance: a connecting panel needs to know about the *previous*
// instance, the one it's replacing.
let panelOnScreen = false;

// The board's detail panel: tabs, resizable width, closing via Escape or a
// click on the board behind it.
//
// Closing goes through the header's close link (a Turbo Frame navigation
// back to the board) rather than hiding the element, so Escape and the
// button land in the same place. The slide-out runs first, so the panel is
// gone before the frame swaps under it.
export default class extends Controller {
  static targets = ["closeLink", "tab", "tabPanel"];

  connect() {
    // No stored width means the stylesheet's default (two thirds of the board)
    // stands — don't overwrite it with a number.
    if (this.storedWidth()) this.applyWidth(this.storedWidth());

    if (!panelOnScreen) this.slideIn();
    panelOnScreen = true;

    // Move focus into the panel so keyboard users continue from the panel
    // rather than from wherever the card was in the board.
    this.element.focus({ preventScroll: true });
  }

  // The header's button is the close link itself, so it would navigate without
  // sliding: hold it back for the animation, then let close() click it through.
  closeFromLink(event) {
    if (this.leaving) return;

    event.preventDefault();
    this.close();
  }

  closeOnEscape(event) {
    if (event.key !== "Escape") return;

    this.close();
  }

  // Notion-style click-away close. Left alone: a click inside the panel, and
  // one targeting its own frame (opening another application), which
  // closing first would cancel.
  closeOnOutsideClick(event) {
    if (this.element.contains(event.target)) return;
    if (event.target.closest?.(`[data-turbo-frame="${this.frameId}"]`)) return;

    this.close();
  }

  get frameId() {
    return this.element.closest("turbo-frame")?.id;
  }

  // --- Sliding ------------------------------------------------------------

  slideIn() {
    this.element.classList.add("detail-panel--entering");
    this.whenSettled(() => this.element.classList.remove("detail-panel--entering"));
  }

  close() {
    if (this.leaving) return;

    this.leaving = true;
    this.element.classList.remove("detail-panel--entering");
    this.element.classList.add("detail-panel--leaving");
    this.whenSettled(() => this.closeLinkTarget.click());
  }

  // Runs callback once animations finish — right away when there are none,
  // which is what reduced motion leaves behind.
  whenSettled(callback) {
    const animations = this.element.getAnimations();
    if (animations.length === 0) return callback();

    Promise.all(animations.map((animation) => animation.finished))
      .then(callback)
      // A cancelled animation means the element is already gone.
      .catch(() => {});
  }

  // --- Tabs ---------------------------------------------------------------

  selectTab(event) {
    this.activate(event.currentTarget.dataset.tab);
  }

  // Left/right walks the tab strip, as expected of a tablist.
  moveTabFocus(event) {
    const offset = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!offset) return;

    event.preventDefault();

    const tabs = this.tabTargets;
    const next = tabs[(tabs.indexOf(event.currentTarget) + offset + tabs.length) % tabs.length];

    this.activate(next.dataset.tab);
    next.focus();
  }

  activate(key) {
    this.tabTargets.forEach((tab) => {
      const selected = tab.dataset.tab === key;

      tab.setAttribute("aria-selected", selected);
      tab.tabIndex = selected ? 0 : -1;
    });

    this.tabPanelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.tab !== key;
    });
  }

  // --- Width --------------------------------------------------------------

  startResize(event) {
    // preventDefault stops text selection, but also swallows the focus a
    // click would give the handle, so focus it explicitly for keyboard resize.
    event.preventDefault();
    event.currentTarget.focus();

    // A drag ending past the panel's edge dispatches its click outside the
    // panel, which closeOnOutsideClick would read as a click away. Swallow it.
    this.onSwallowClick = (clickEvent) => clickEvent.stopPropagation();
    window.addEventListener("click", this.onSwallowClick, { capture: true, once: true });

    this.onPointerMove = (moveEvent) => this.applyWidth(window.innerWidth - moveEvent.clientX);
    this.onPointerUp = () => this.stopResize();

    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp, { once: true });
    this.element.classList.add("detail-panel--resizing");
  }

  stopResize() {
    window.removeEventListener("pointermove", this.onPointerMove);
    this.onPointerMove = null;
    this.element.classList.remove("detail-panel--resizing");
    this.storeWidth();

    // A touch drag ends here with no click ever dispatched, so `once: true`
    // never fires and the listener would sit on `window` forever without this.
    // A timeout, not immediate cleanup, still catches a mouse drag's phantom
    // click — it fires synchronously, well ahead of the timeout.
    this.swallowClickTimeout = window.setTimeout(() => this.stopSwallowingClicks(), 0);
  }

  // Idempotent, since it can run from the timeout above, again from
  // disconnect() below, or from both.
  stopSwallowingClicks() {
    if (this.swallowClickTimeout) {
      window.clearTimeout(this.swallowClickTimeout);
      this.swallowClickTimeout = null;
    }

    if (!this.onSwallowClick) return;

    window.removeEventListener("click", this.onSwallowClick, { capture: true });
    this.onSwallowClick = null;
  }

  resizeWithKeyboard(event) {
    const offset = { ArrowLeft: KEYBOARD_STEP, ArrowRight: -KEYBOARD_STEP }[event.key];
    if (!offset) return;

    event.preventDefault();
    this.applyWidth(this.currentWidth() + offset);
    this.storeWidth();
  }

  // Back to the stylesheet's default, rather than to a width this controller
  // happens to think is the default.
  resetWidth() {
    this.element.style.removeProperty(WIDTH_PROPERTY);
    window.localStorage.removeItem(WIDTH_STORAGE_KEY);
  }

  disconnect() {
    // Read after the swap has settled: a panel replacing this one is already in
    // place by then, while a close leaves the board without one.
    window.requestAnimationFrame(() => {
      panelOnScreen = Boolean(document.querySelector(".detail-panel"));
    });

    // A resize that never got a pointerup (a cancelled touch drag, say) never
    // ran stopResize, so this is also the only place its click swallower is
    // guaranteed to still get cleared. A no-op when there's nothing to clear.
    this.stopSwallowingClicks();

    if (!this.onPointerMove) return;

    // Mid-drag teardown: drop the listener, but don't record a width — the
    // element is on its way out and would measure as zero.
    window.removeEventListener("pointermove", this.onPointerMove);
    this.onPointerMove = null;
  }

  applyWidth(width) {
    // Set as a custom property rather than a width so the stylesheet keeps
    // owning how the panel is laid out; this only feeds it a number.
    this.element.style.setProperty(WIDTH_PROPERTY, `${this.clamp(width)}px`);
  }

  currentWidth() {
    return this.element.getBoundingClientRect().width;
  }

  clamp(width) {
    return Math.round(Math.min(Math.max(width, MIN_WIDTH), window.innerWidth * 0.8));
  }

  storedWidth() {
    return Number(window.localStorage.getItem(WIDTH_STORAGE_KEY)) || null;
  }

  storeWidth() {
    const width = this.currentWidth();
    if (width <= 0) return;

    window.localStorage.setItem(WIDTH_STORAGE_KEY, width);
  }
}
